import {
  MappingConfiguration,
  MappingPreviewSnapshot,
  MappingPreviewRow,
  MandatoryFieldsMapping,
  SharePointCredentials,
  DashboardStats,
  DashboardActionItem,
  DashboardPhaseSummary,
  DashboardRole,
  DashboardWizardSummary
} from '../../../shared/types';

export type {
  MappingPreviewSnapshot,
  MappingPreviewRow,
  MandatoryFieldsMapping,
  SharePointCredentials,
  DashboardStats,
  DashboardActionItem,
  DashboardPhaseSummary,
  DashboardRole,
  DashboardWizardSummary
};

// Entity Mapping 2.0 Types - defined locally to avoid build issues
export enum MappingStatus {
  DRAFT = 'draft',
  IN_PROGRESS = 'in_progress',
  COMPLETE = 'complete',
  VALIDATED = 'validated',
  LOCKED = 'locked',
  ARCHIVED = 'archived'
}

export enum ValidationStatus {
  VALID = 'valid',
  WARNING = 'warning',
  ERROR = 'error',
  NOT_VALIDATED = 'not_validated'
}

export enum MappingType {
  DIRECT = 'direct',
  TRANSFORMED = 'transformed',
  CONSTANT = 'constant',
  CALCULATED = 'calculated',
  LOOKUP = 'lookup'
}

export interface TransformationRule {
  type: string;
  parameters: Record<string, any>;
  expression?: string;
  description?: string;
}

export interface FieldMappingTableRow {
  id: string;
  targetField: string;
  targetFieldLabel?: string;
  sourceField?: string;
  sourceFileId?: string;
  constantValue?: string;
  transformation?: TransformationRule;
  mappingType: MappingType;
  required: boolean;
  status: ValidationStatus;
  notes?: string;
}

export interface ValueMappingTableRow {
  id: string;
  sourceValue: string;
  targetValue: string;
  count?: number;
  status: 'mapped' | 'unmapped' | 'pending';
  notes?: string;
}

export interface MappingRule {
  id: string;
  name: string;
  condition: string;
  priority: number;
  enabled: boolean;
  fieldMappings: FieldMappingTableRow[];
  valueMappings?: { [fieldName: string]: ValueMappingTableRow[] };
  createdAt: string;
  updatedAt: string;
}

export interface EntityMappingV2 {
  entityName: string;
  fieldMappings: FieldMappingTableRow[];
  valueMappings: { [fieldName: string]: ValueMappingTableRow[] };
  rules: MappingRule[];
  status: MappingStatus;
  isDraft: boolean;
  lastModified: string;
  lastModifiedBy?: string;
  validationResults?: any[];
  lockedBy?: string;
  lockedAt?: string;
  completedAt?: string;
  lastValidatedAt?: string;
  statusHistory?: any[];
  metadata?: {
    importedFrom?: string;
    exportedAt?: string;
    version?: string;
  };
}

export interface Customer {
  id: string;
  name: string;
  description?: string;
  industry?: string;
  region?: string;
  createdAt: string;
  updatedAt: string;
  archived?: boolean;
  archivedAt?: string;
  sharepoint?: {
    connected: boolean;
    siteUrl?: string;
    driveId?: string;
    tenantId?: string;
    appId?: string;
    connectedAt?: string;
  };
}

export interface Phase {
  id: string;
  customerId: string;
  name: string;
  description?: string;
  status: PhaseStatus;
  type: PhaseType;
  createdAt: string;
  updatedAt: string;
}

export enum PhaseStatus {
  CREATED = 'created',
  CONFIGURED = 'configured',
  METADATA_FETCHED = 'metadata_fetched',
  FILES_UPLOADED = 'files_uploaded',
  MAPPING_IN_PROGRESS = 'mapping_in_progress',
  MAPPING_COMPLETE = 'mapping_complete',
  VALIDATED = 'validated',
  EXPORT_READY = 'export_ready',
  COMPLETED = 'completed',
  FAILED = 'failed'
}

export enum PhaseType {
  MIGRATION = 'migration',
  UAT = 'uat',
  PRODUCTION = 'production'
}

export interface SharePointPreviewResponse {
  fileId: string;
  fileName: string;
  header: string[];
  rows: Record<string, any>[];
}

export interface EntityMappingVersionSummary {
  versionId: string;
  createdAt: string;
  createdBy?: string;
  status?: string;
}

export interface EntityMappingVersionDetail {
  versionId: string;
  entityName: string;
  createdAt: string;
  createdBy?: string;
  status?: string;
  notes?: string;
  mappings: MappingConfiguration[];
}

export interface WizardComponentProps {
  isEmbedded?: boolean;
  onFailure?: (message: string) => void;
}